import {
  resolvePlayerAppVersionForTelemetry,
  shouldPreferNativeShellPlayerAppVersion
} from '@lucimark/shared/helpers/playerAppVersion'
import { VERSION } from '@/constants'
import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatformInstance, whenPlatformReady } from '@/platform'

export { isTauriRuntime } from '@/helpers/isTauriRuntime'

/** Core invoke: PackageManager on Android, `None` elsewhere. Safe before `initPlatform()`. */
async function getAndroidPackageVersionFromCoreCommand(): Promise<string | null> {
  if (typeof window === 'undefined' || !isTauriRuntime()) {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    const fromCmd = await invoke<string | null>('android_package_version').catch(() => null)
    if (typeof fromCmd === 'string' && fromCmd.trim() !== '') {
      return fromCmd.trim()
    }
  }
  catch {
    /* fall through */
  }
  return null
}

function parsePluginGetAppVersionResponse(raw: unknown): string | null {
  if (raw == null) {
    return null
  }
  if (typeof raw === 'string') {
    const t = raw.trim()
    return t.length > 0 ? t : null
  }
  if (typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (typeof o.version === 'string' && o.version.trim() !== '') {
    return o.version.trim()
  }
  if (o.value != null) {
    return parsePluginGetAppVersionResponse(o.value)
  }
  return null
}

/**
 * Android: Rust `android_package_version` (PackageManager `versionName`) then plugin IPC.
 *
 * Core command is safe before `initPlatform()` (desktop returns null). Plugin IPC stays gated on
 * `getPlatform() === 'android'` (regression in cf45f795: desktop hitting Android-only invokes).
 */
async function getAndroidPackageVersionFromPlugin(): Promise<string | null> {
  const fromCmd = await getAndroidPackageVersionFromCoreCommand()
  if (fromCmd != null) {
    return fromCmd
  }
  if (typeof window === 'undefined' || !isTauriRuntime()) {
    return null
  }
  const { getPlatform } = await import('@/platform')
  if (getPlatform() !== 'android') {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    const raw = await invoke<unknown>('plugin:lucimark-android|getAppVersion').catch(() => null)
    return parsePluginGetAppVersionResponse(raw)
  }
  catch {
    return null
  }
}

async function getNativeShellVersionStringForMerge(): Promise<string | null> {
  const fromPlugin = await getAndroidPackageVersionFromPlugin()
  if (fromPlugin != null) {
    return fromPlugin
  }
  try {
    const info = await getPlatformInstance().app.getInfo()
    const native = info?.version?.trim() ?? ''
    if (native.length > 0) {
      return native
    }
  }
  catch {
    /* fall through */
  }
  // Before initPlatform() the platform instance may still be web (getInfo → null) while IPC is already up.
  if (isTauriRuntime()) {
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      const info = await invoke<{ version?: string } | null>('app_get_info').catch(() => null)
      const v = info?.version?.trim() ?? ''
      if (v.length > 0) {
        return v
      }
    }
    catch {
      return null
    }
  }
  return null
}

/**
 * App version sent as `x-player-version` (handshake, REST, heartbeats).
 * Android: PackageManager `versionName` only — Web OTA `package.json` must not outrun the shell
 * or APK OTA skips the install. Desktop: max-merge embedded JS with crate version.
 * Running JS semver is sent separately as `x-player-embedded-version` for Web OTA `minAppVersion`.
 * Overlay **App v** reads the shell via {@link getNativeShellAppVersionName}, not this merge.
 * Waits for `initPlatform()` on Tauri so plugin IPC / platform are correct; core
 * `android_package_version` can still prefer the shell before that.
 */
export async function getResolvedPlayerAppVersion(): Promise<string> {
  const embedded = VERSION
  if (typeof window === 'undefined' || !isTauriRuntime()) {
    return embedded
  }
  await whenPlatformReady()
  try {
    const androidPkg = await getAndroidPackageVersionFromCoreCommand()
    const native = androidPkg ?? await getNativeShellVersionStringForMerge()
    const { getPlatform } = await import('@/platform')
    return resolvePlayerAppVersionForTelemetry({
      embedded,
      nativeVersion: native,
      preferNativeShell: shouldPreferNativeShellPlayerAppVersion({
        platform: getPlatform(),
        androidPackageVersionName: androidPkg
      })
    })
  }
  catch {
    const { getPlatform } = await import('@/platform')
    if (getPlatform() === 'android') {
      return ''
    }
    return embedded
  }
}

/**
 * Native shell `versionName` / bundle version only (no merge with embedded web assets).
 * Android: plugin → PackageManager; other Tauri: `app.getInfo()`.
 */
export async function getNativeShellAppVersionName(): Promise<string | null> {
  if (typeof window === 'undefined' || !isTauriRuntime()) {
    return null
  }
  await whenPlatformReady()
  const fromPlugin = await getAndroidPackageVersionFromPlugin()
  if (fromPlugin != null) {
    return fromPlugin
  }
  try {
    const info = await getPlatformInstance().app.getInfo()
    const native = info?.version?.trim() ?? ''
    return native.length > 0 ? native : null
  }
  catch {
    return null
  }
}
